import { useState, useEffect, useCallback } from 'react';
import offlineDB from '@/lib/offlineDatabase';

/**
 * Hook para gerenciar modo offline do PDV
 */
export const useOfflineMode = () => {
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  const [isInitialized, setIsInitialized] = useState(false);
  const [syncStatus, setSyncStatus] = useState({
    lastSync: null,
    pendingSales: 0,
    productsCount: 0,
    isSyncing: false,
  });

  // Inicializar banco de dados
  useEffect(() => {
    const init = async () => {
      try {
        await offlineDB.init();
        setIsInitialized(true);
        
        // Carregar estatísticas
        const stats = await offlineDB.getStats();
        setSyncStatus(prev => ({
          ...prev,
          lastSync: stats.last_sync,
          pendingSales: stats.pending_sales,
          productsCount: stats.products_count,
        }));
        
        // Se online, sincronizar
        if (navigator.onLine) {
          offlineDB.syncWithServer();
        }
      } catch (error) {
        console.error('[useOfflineMode] Erro ao inicializar:', error);
      }
    };
    
    init();
  }, []);

  // Monitorar status de conexão
  useEffect(() => {
    const handleOnline = () => {
      setIsOnline(true);
      offlineDB.syncWithServer();
    };
    
    const handleOffline = () => {
      setIsOnline(false);
    };
    
    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);
    
    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  // Atualizar estatísticas periodicamente
  useEffect(() => {
    const interval = setInterval(async () => {
      if (isInitialized) {
        const stats = await offlineDB.getStats();
        setSyncStatus(prev => ({
          ...prev,
          lastSync: stats.last_sync,
          pendingSales: stats.pending_sales,
          productsCount: stats.products_count,
        }));
      }
    }, 30000); // A cada 30 segundos
    
    return () => clearInterval(interval);
  }, [isInitialized]);

  // Sincronizar manualmente
  const syncNow = useCallback(async () => {
    if (!isOnline) {
      throw new Error('Sem conexão com a internet');
    }
    
    setSyncStatus(prev => ({ ...prev, isSyncing: true }));
    
    try {
      await offlineDB.forceSync();
      const stats = await offlineDB.getStats();
      setSyncStatus({
        lastSync: stats.last_sync,
        pendingSales: stats.pending_sales,
        productsCount: stats.products_count,
        isSyncing: false,
      });
    } catch (error) {
      setSyncStatus(prev => ({ ...prev, isSyncing: false }));
      throw error;
    }
  }, [isOnline]);

  // Buscar produtos (local ou remoto)
  const getProducts = useCallback(async () => {
    if (!isOnline && isInitialized) {
      // Offline: buscar do banco local
      return await offlineDB.getProducts();
    }
    return null; // Indica que deve buscar da API
  }, [isOnline, isInitialized]);

  // Buscar produto por código de barras
  const getProductByBarcode = useCallback(async (barcode) => {
    if (!isOnline && isInitialized) {
      return await offlineDB.getProductByBarcode(barcode);
    }
    return null;
  }, [isOnline, isInitialized]);

  // Buscar clientes (local ou remoto)
  const getCustomers = useCallback(async () => {
    if (!isOnline && isInitialized) {
      return await offlineDB.getCustomers();
    }
    return null;
  }, [isOnline, isInitialized]);

  // Salvar venda (local se offline)
  const saveSale = useCallback(async (saleData) => {
    if (!isOnline) {
      // Offline: salvar localmente
      const localId = await offlineDB.saveOfflineSale(saleData);
      return { success: true, offline: true, local_id: localId };
    }
    return null; // Indica que deve salvar via API
  }, [isOnline]);

  // Contar vendas pendentes
  const getPendingSalesCount = useCallback(async () => {
    return await offlineDB.countPendingSales();
  }, []);

  // Sincronizar produtos do servidor para local
  const cacheProducts = useCallback(async (products) => {
    await offlineDB.saveProducts(products);
    const stats = await offlineDB.getStats();
    setSyncStatus(prev => ({
      ...prev,
      productsCount: stats.products_count,
    }));
  }, []);

  // Sincronizar clientes do servidor para local
  const cacheCustomers = useCallback(async (customers) => {
    await offlineDB.saveCustomers(customers);
  }, []);

  return {
    isOnline,
    isInitialized,
    syncStatus,
    syncNow,
    getProducts,
    getProductByBarcode,
    getCustomers,
    saveSale,
    getPendingSalesCount,
    cacheProducts,
    cacheCustomers,
  };
};

export default useOfflineMode;
